import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { NPIEntryStep } from './NPIEntryStep';
import { DriversLicenseUploadStep } from './DriversLicenseUploadStep';
import { SelfieCaptureStep } from './SelfieCaptureStep';
import { ESignaturePadStep } from './ESignaturePadStep';
import { ReviewSubmitStep } from './ReviewSubmitStep';
import { apiClient } from '../../lib/api';
import { TELEMED_AGREEMENT_VERSION } from '../../content/telemedAgreement';

/**
 * IDProofingStepWrapper Component
 * 
 * Wrapper that displays the appropriate step component based on the current route
 * and manages the data flow between steps and localStorage persistence.
 */

interface IDProofingStepWrapperProps {
  physicianId: string;
  initialNPI?: string;
  onComplete: () => void;
  onCancel: () => void;
  // Hybrid clinic (own NPI + network for uncovered states): the signature step
  // also executes the Contract + BAA, so record the agreement version.
  usesNetwork?: boolean;
}

interface StepData {
  npi: string;
  driversLicense: File | null;
  selfiePhoto: Blob | null;
  signature: Blob | null;
  stepStatuses: Record<string, 'pending' | 'in_progress' | 'completed'>;
  lastUpdated: number;
}

export const IDProofingStepWrapper: React.FC<IDProofingStepWrapperProps> = ({
  physicianId,
  initialNPI = '',
  onComplete,
  onCancel: _onCancel,
  usesNetwork = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // In-memory state for file objects (since they can't be stored in localStorage)
  const [fileData, setFileData] = useState<{
    driversLicense: File | null;
    selfiePhoto: Blob | null;
    signature: Blob | null;
  }>({
    driversLicense: null,
    selfiePhoto: null,
    signature: null
  });

  // Extract current step from URL
  const getCurrentStep = (): string => {
    const pathname = location.pathname;
    if (pathname.includes('/npi')) return 'npi';
    if (pathname.includes('/license')) return 'license';
    if (pathname.includes('/selfie')) return 'selfie';
    if (pathname.includes('/signature')) return 'signature';
    if (pathname.includes('/review')) return 'review';
    return 'dashboard';
  };

  const currentStep = getCurrentStep();

  // Load and save data helpers
  const loadStepData = (): StepData => {
    try {
      const saved = localStorage.getItem(`idproofing_data_${physicianId}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.lastUpdated && (Date.now() - parsed.lastUpdated) < 7 * 24 * 60 * 60 * 1000) {
          return {
            ...parsed,
            // Merge with in-memory file data
            driversLicense: fileData.driversLicense,
            selfiePhoto: fileData.selfiePhoto,
            signature: fileData.signature
          };
        }
      }
    } catch (error) {
      console.warn('Failed to load step data:', error);
    }
    
    return {
      npi: initialNPI,
      driversLicense: fileData.driversLicense,
      selfiePhoto: fileData.selfiePhoto,
      signature: fileData.signature,
      stepStatuses: {},
      lastUpdated: Date.now()
    };
  };

  const saveStepData = (data: Partial<StepData>) => {
    try {
      const currentData = loadStepData();
      const updatedData = {
        ...currentData,
        ...data,
        lastUpdated: Date.now()
      };
      localStorage.setItem(`idproofing_data_${physicianId}`, JSON.stringify(updatedData));
    } catch (error) {
      console.warn('Failed to save step data:', error);
    }
  };

  const markStepComplete = (stepId: string) => {
    const currentData = loadStepData();
    saveStepData({
      stepStatuses: {
        ...currentData.stepStatuses,
        [stepId]: 'completed'
      }
    });
  };

  // Navigation helpers
  const goBackToDashboard = () => {
    navigate('/id-proofing');
  };

  const goToNextStep = () => {
    const stepOrder = ['npi', 'license', 'selfie', 'signature', 'review'];
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex < stepOrder.length - 1) {
      navigate(`/id-proofing/${stepOrder[currentIndex + 1]}`);
    } else {
      goBackToDashboard();
    }
  };

  // Step-specific handlers
  const handleNPIUpdate = (npi: string) => {
    saveStepData({ npi });
    if (npi) {
      markStepComplete('npi');
    }
  };

  const handleFileUpload = async (file: File | null) => {
    if (!file) {
      setFileData(prev => ({ ...prev, driversLicense: null }));
      saveStepData({ driversLicense: null });
      return;
    }

    try {
      console.log('🚗 Uploading drivers license...');
      const response = await apiClient.uploadDriversLicense(physicianId, file);
      
      if (response.success) {
        console.log('🚗 Drivers license uploaded successfully');
        setFileData(prev => ({ ...prev, driversLicense: file }));
        markStepComplete('license');
      } else {
        console.error('🚗 Failed to upload drivers license:', response.error);
        alert(`Failed to upload drivers license: ${response.error}`);
      }
    } catch (error) {
      console.error('🚗 Error uploading drivers license:', error);
      alert('Failed to upload drivers license. Please try again.');
    }
  };

  const handlePhotoCapture = async (photo: Blob | null) => {
    if (!photo) {
      setFileData(prev => ({ ...prev, selfiePhoto: null }));
      saveStepData({ selfiePhoto: null });
      return;
    }

    try {
      console.log('📸 Uploading selfie photo...');
      const response = await apiClient.uploadSelfie(physicianId, photo);
      
      if (response.success) {
        console.log('📸 Selfie photo uploaded successfully');
        setFileData(prev => ({ ...prev, selfiePhoto: photo }));
        markStepComplete('selfie');
      } else {
        console.error('📸 Failed to upload selfie photo:', response.error);
        alert('Failed to upload selfie photo. Please try again.');
      }
    } catch (error) {
      console.error('📸 Error uploading selfie photo:', error);
      alert('Failed to upload selfie photo. Please try again.');
    }
  };

  const handleSignatureCapture = async (signature: Blob | null) => {
    if (!signature) {
      setFileData(prev => ({ ...prev, signature: null }));
      saveStepData({ signature: null });
      return;
    }

    try {
      console.log('🖋️ Uploading signature...');
      const response = await apiClient.uploadSignature(
        physicianId,
        signature,
        usesNetwork ? TELEMED_AGREEMENT_VERSION : undefined
      );
      
      if (response.success) {
        console.log('🖋️ Signature uploaded successfully');
        setFileData(prev => ({ ...prev, signature: signature }));
        markStepComplete('signature');
        
        // Show success toast
        const { toast } = await import('../../lib/toast');
        toast.success('Signature uploaded successfully!');
      } else {
        console.error('🖋️ Failed to upload signature:', response.error);
        
        // Show error toast with API error message
        const { toast } = await import('../../lib/toast');
        toast.error(`Signature upload failed: ${response.error}`, { duration: 7000 });
      }
    } catch (error) {
      console.error('🖋️ Error uploading signature:', error);
      
      // Show error toast for network/unexpected errors
      const { toast } = await import('../../lib/toast');
      toast.error('Signature upload failed: Network error or unexpected issue', { duration: 7000 });
    }
  };

  const handleFinalSubmit = async () => {
    if (isSubmitting) return;

    const stepData = loadStepData();
    const stepStatuses = stepData.stepStatuses || {};

    // Check completion by step status instead of file data (Blobs can't be serialized)
    const requiredSteps = ['npi', 'license', 'selfie', 'signature'];
    const missingSteps = requiredSteps.filter(step => stepStatuses[step] !== 'completed');

    if (missingSteps.length > 0) {
      alert(`Please complete all steps before submitting. Missing: ${missingSteps.join(', ')}`);
      return;
    }

    setIsSubmitting(true);
    try {
      console.log('🚀 Finalizing ID proofing submission...');
      const response = await apiClient.submitIdProofing(physicianId);

      // An "already submitted" rejection means a prior attempt succeeded —
      // land the user on the dashboard instead of a retry dead-end.
      const alreadySubmitted = !response.success && /already submitted/i.test(response.error || '');

      if (response.success || alreadySubmitted) {
        console.log('🚀 ID proofing finalized successfully');
        // Clear saved data on successful submission
        localStorage.removeItem(`idproofing_data_${physicianId}`);
        alert('ID proofing submitted successfully! Your submission is being reviewed.');
        onComplete();
      } else {
        console.error('🚀 Failed to finalize ID proofing:', response.error);
        alert(`Submission failed: ${response.error || 'Please try again.'}`);
      }
    } catch (error) {
      console.error('🚀 Error finalizing ID proofing:', error);
      alert('Submission failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get current step data
  const stepData = loadStepData();

  // Debug logging for review step
  if (currentStep === 'review') {
    console.log('📋 IDProofingStepWrapper stepData for review:', {
      npi: stepData.npi,
      driversLicense: !!stepData.driversLicense,
      selfiePhoto: !!stepData.selfiePhoto,
      signature: !!stepData.signature,
      stepData
    });
  }

  // Render appropriate step component
  if (currentStep === 'npi') {
    return (
      <NPIEntryStep
        currentNPI={stepData.npi}
        onNext={goToNextStep}
        onBack={goBackToDashboard}
        onNPIUpdate={handleNPIUpdate}
        isLoading={false}
      />
    );
  }

  if (currentStep === 'license') {
    return (
      <DriversLicenseUploadStep
        onNext={goToNextStep}
        onBack={goBackToDashboard}
        onFileUpload={handleFileUpload}
        uploadedFile={stepData.driversLicense}
        isLoading={false}
      />
    );
  }

  if (currentStep === 'selfie') {
    return (
      <SelfieCaptureStep
        onNext={goToNextStep}
        onBack={goBackToDashboard}
        onPhotoCapture={handlePhotoCapture}
        capturedPhoto={stepData.selfiePhoto}
        isLoading={false}
      />
    );
  }

  if (currentStep === 'signature') {
    return (
      <ESignaturePadStep
        onNext={goToNextStep}
        onBack={goBackToDashboard}
        onSignatureCapture={handleSignatureCapture}
        capturedSignature={stepData.signature}
        isLoading={false}
        usesNetwork={usesNetwork}
      />
    );
  }

  if (currentStep === 'review') {
    return (
      <ReviewSubmitStep
        npi={stepData.npi}
        driversLicense={stepData.driversLicense}
        selfiePhoto={stepData.selfiePhoto}
        signature={stepData.signature}
        onBack={goBackToDashboard}
        onSubmit={handleFinalSubmit}
        isSubmitting={isSubmitting}
      />
    );
  }

  // Default fallback - should not happen
  return <div>Unknown step</div>;
}; 